import { useState } from 'react';
import { api } from '../api.js';

// Blocking modal for existing users who pre-date the Terms + Privacy
// requirement, or who need to re-accept after a version bump. Mounted
// at the shell level so it covers the whole app — the keeper can't
// reach Home, settings, or anything else until they either accept or
// sign out.
//
// Reefer-voice copy: short summaries inline, link out for the full doc.
// Most users won't click through; we just want informed consent that
// they know what they're agreeing to.
export function TermsAcceptModal({ user, onAccepted, onSignOut }) {
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const confirm = async () => {
    if (!accepted) return;
    setBusy(true);
    setErr('');
    try {
      await api('/api/auth/accept-terms', { method: 'POST' });
      await onAccepted();
    } catch (e) {
      setErr(e.message || 'Could not record acceptance.');
    } finally {
      setBusy(false);
    }
  };

  const isUpdate = !!user?.terms_accepted_at;

  return (
    <div className="tam-overlay" role="dialog" aria-modal="true" aria-labelledby="tam-title">
      <div className="tam-sheet">
        <div className="tam-eyebrow">{isUpdate ? 'Updated terms' : 'Welcome aboard'}</div>
        <h2 id="tam-title" className="tam-title font-heading">
          {isUpdate ? "We've updated the legal bits" : 'A quick note before you start'}
        </h2>
        <p className="tam-intro">
          {isUpdate
            ? "We updated our Terms of Use and Privacy Policy. Reefer-voice as always — no legalese where we could help it. Here's the gist:"
            : 'We need you to confirm a couple of things before we open the doors. Reefer-voice as always — no legalese where we could help it.'}
        </p>

        <div className="tam-section">
          <div className="tam-section-head">Terms of Use</div>
          <p>
            TankSensei helps you keep track of your tank.{' '}
            <strong>Sensei is an AI helper, not a chemist</strong> — useful, sometimes wrong, never
            a replacement for your own judgement on what goes in your tank. Dose calculator math is
            grounded in published BRS reefer values, but you preview and add the liquid yourself.{' '}
            <strong>Alerts can arrive late or not at all</strong>, so keep your own safeguards. You
            must be 18 or older, and the terms limit what Printed Reef Inc. is liable for.
          </p>
          <a className="tam-link" href="/terms" target="_blank" rel="noopener noreferrer">
            Read the full terms →
          </a>
        </div>

        <div className="tam-section">
          <div className="tam-section-head">Privacy Policy</div>
          <p>
            <strong>Your tank data is yours.</strong> We don't sell it, don't share it with
            advertisers, don't use third-party trackers. Service providers run parts of the app for
            us (hosting, AI, email, text alerts, the display connection, and HYDROS if you connect
            it); the full list is in the policy. Export or delete any time from settings.
          </p>
          <a className="tam-link" href="/privacy" target="_blank" rel="noopener noreferrer">
            Read the full privacy policy →
          </a>
        </div>

        <label className="tam-check">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
          />
          <span>
            I'm 18 or older, and I've read and agree to the Terms of Use and Privacy Policy.
          </span>
        </label>

        {err && (
          <p className="tam-err" role="alert">
            {err}
          </p>
        )}

        <div className="tam-actions">
          <button type="button" className="tam-signout" onClick={onSignOut} disabled={busy}>
            Sign out
          </button>
          <button
            type="button"
            className="tam-accept"
            onClick={confirm}
            disabled={!accepted || busy}
          >
            {busy ? 'Saving…' : 'Accept and continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
